import React from 'react';

import { lazyWithReload } from '@/lib/chunkRecovery';
import { useBreakpointsCtx } from '@/providers/BreakpointsProvider/BreakpointsContext.hooks';
import { DashboardTourProvider, useDashboardTour } from '@/providers/DashboardTourContext';
import { HeaderProvider } from '@/providers/HeaderContextProvider/HeaderContextProvider';
import { BannerProvider } from '@/providers/BannerProvider/BannerContextProvider';
import { HeaderWithNavigation } from '@/layouts/MainLayout/components/HeaderWithNavigation/HeaderWithNavigation';
import { StyledAppContainer, StyledMain } from '@/layouts/MainLayout/MainLayout.styled';
import { THeader } from '@/layouts/MainLayout/utils/types';

// MainLayout renders on every authenticated route, so a static import would put
// react-joyride in the eager bundle for a tour most users never see
const DashboardTour = lazyWithReload(
  () => import('@/layouts/MainLayout/components/DashboardTour/DashboardTour'),
);

interface LayoutProps {
  children?: React.ReactNode;
  header?: THeader;
  isFlush?: boolean;
}

const MainLayout: React.FC<LayoutProps> = ({ children, header, isFlush }) => {
  const { isDesktop } = useBreakpointsCtx();

  const shouldRenderFromTop = !header;

  const { isTourActive } = useDashboardTour();

  return (
    <StyledAppContainer className="default-layout">
      {isTourActive && (
        <React.Suspense fallback={null}>
          <DashboardTour />
        </React.Suspense>
      )}
      <HeaderProvider key={header}>
        <HeaderWithNavigation header={header} />
        <BannerProvider>
          <StyledMain
            className="main"
            shouldRenderFromTop={shouldRenderFromTop}
            isDesktop={isDesktop}
            isFlush={isFlush}
          >
            {children}
          </StyledMain>
        </BannerProvider>
      </HeaderProvider>
    </StyledAppContainer>
  );
};

export default function MainLayoutWithProvider(props: LayoutProps) {
  return (
    <DashboardTourProvider>
      <MainLayout {...props} />
    </DashboardTourProvider>
  );
}
